import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { CourseContext } from "./CourseContext";
import { SliderBigText } from "./SliderBigText";
import { TrainingProgram } from "./TrainingProgram";
import s from '../CourseDetail.module.css';
import { useAppTranslation } from "./TranslationContext";
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

export const CourseDetail = () => {
  const { user, handleLang } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const courses = useContext(CourseContext);
  const course = courses.find(c => c.id === parseInt(id, 10));
  const { t } = useAppTranslation();

  const [dataAvz, setDataAvz] = useState([]);
  const [avzalliklar, setAvzalliklar] = useState([]);

  const [dataDesc, setDataDesc] = useState([]);
  const [description, setDescription] = useState({});

  const [loading, setLoading] = useState(true);
  const [loadingDesc, setLoadingDesc] = useState(true);
  const [error, setError] = useState(null);
  const [errorDesc, setErrorDesc] = useState(null);

  const [author, setAuthor] = useState(null);
  const [loadingAuthor, setLoadingAuthor] = useState(false);
  const [errorAuthor, setErrorAuthor] = useState(null);

  // защита от двойных кликов по "Записаться"
  const [enrolling, setEnrolling] = useState(false);

  // Загружаем карточки и описание
  useEffect(() => {
    fetch("https://api.eduskill.uz/api/kurs-avzallik/")
      .then((res) => {
        if (!res.ok) throw new Error((t('networkError') || 'Сетевая ошибка: ') + res.status);
        return res.json();
      })
      .then((json) => {
        setDataAvz(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });

    fetch("https://api.eduskill.uz/api/kurs-big-description/")
      .then((res) => {
        if (!res.ok) throw new Error((t('networkError') || 'Сетевая ошибка: ') + res.status);
        return res.json();
      })
      .then((json) => {
        setDataDesc(json);
        setLoadingDesc(false);
      })
      .catch((err) => {
        setErrorDesc(err.message);
        setLoadingDesc(false);
      });
  }, [t]);

  // Обработка avzalliklar
  useEffect(() => {
    if (!loading && Array.isArray(dataAvz) && dataAvz.length > 0) {
      const filtered = dataAvz
        .filter(item => String(item?.kurs?.id) === String(id))
        .map(i => ({
          id: i.id,
          kurs_id: i.kurs.id,
          title_uz: i.nomi_uz,
          title_ru: i.nomi_ru,
          title_en: i.nomi_en,
          desc_uz: i.description_uz,
          desc_ru: i.description_ru,
          desc_en: i.description_en,
          img_uz: i.img_uz,
          img_ru: i.img_ru,
          img_en: i.img_en
        }));
      setAvzalliklar(filtered);
    }
  }, [dataAvz, loading, id]);

  // Обработка description
  useEffect(() => {
    if (!loadingDesc && Array.isArray(dataDesc) && dataDesc.length > 0) {
      const desc = dataDesc.find(i => String(i?.kurs?.id) === String(id));
      if (desc) {
        setDescription({
          id: desc.id,
          kurs_id: desc.kurs.id,
          title_uz: desc.nomi_uz,
          title_ru: desc.nomi_ru,
          title_en: desc.nomi_en,
          img_uz: desc.img_uz,
          img_ru: desc.img_ru,
          img_en: desc.img_en,
          desc_uz: desc.description_uz,
          desc_ru: desc.description_ru,
          desc_en: desc.description_en
        });
      } else {
        setDescription({});
      }
    }
  }, [dataDesc, loadingDesc, id]);

  // Загружаем автора
  useEffect(() => {
    if (course?.author?.id) {
      setLoadingAuthor(true);

      const fetchAuthorData = async () => {
        try {
          const resCustom = await fetch(`https://api.eduskill.uz/api/custom-users/?user=${course.author.id}`);
          if (!resCustom.ok) throw new Error('Ошибка при загрузке custom-user');
          const customData = await resCustom.json();
          const customUser = Array.isArray(customData) ? customData[0] : customData;

          const resUser = await fetch(`https://api.eduskill.uz/api/users/${course.author.id}/`);
          if (!resUser.ok) throw new Error('Ошибка при загрузке user');
          const userData = await resUser.json();

          setAuthor({ ...customUser, ...userData });
          setLoadingAuthor(false);
        } catch (err) {
          setErrorAuthor(err.message);
          setLoadingAuthor(false);
        }
      };

      fetchAuthorData();
    }
  }, [course]);

  const handleComment = () => {
    const token = localStorage.getItem('token');
    const userId = user?.id;

    if (!token || !userId) {
      toast.warn(t('alertLogin') || 'Пожалуйста, войдите в аккаунт.');
      return;
    }
    navigate(`/courseComment/${id}`);
  };

  const handleEnroll = async () => {
    if (enrolling) return;
  
    const token = localStorage.getItem('token');
    const userId = Number(user?.id); // приводим к числу
  
    if (!token) {
      toast.warn(t('alertLogin') || 'Пожалуйста, войдите в аккаунт.');
      return;
    }
    if (!Number.isFinite(userId) || userId <= 0) {
      // здесь может понадобиться дождаться загрузки профиля
      toast.error(t('profileNotReady') || 'Профиль ещё не загрузился. Попробуйте через пару секунд.');
      return;
    }
  
    try {
      setEnrolling(true);
  
      // Уже записан?
      const checkRes = await fetch(
        `https://api.eduskill.uz/api/user-kurs/?user=${userId}&kurs=${id}`,
        { headers: { 'Authorization': `Token ${token}`, 'Accept': 'application/json' } }
      );
      if (checkRes.ok) {
        const checkData = await checkRes.json().catch(() => null);
        if (Array.isArray(checkData) && checkData.length > 0) {
          navigate(`/themes/${id}`);
          return;
        }
      }
  
      // Берём валидные IDs первой темы и её элемента
      const mavzuRes = await fetch(`https://api.eduskill.uz/api/kurs-mavzulari/?kurs=${id}`, {
        headers: { 'Accept': 'application/json' }
      });
      let mavzuId = null;
      if (mavzuRes.ok) {
        const list = await mavzuRes.json().catch(() => []);
        mavzuId = Array.isArray(list) && list[0]?.id ? Number(list[0].id) : null;
      }
  
      let elementId = null;
      if (mavzuId) {
        const elemRes = await fetch(`https://api.eduskill.uz/api/mavzu-elementi/?mavzu=${mavzuId}`, {
          headers: { 'Accept': 'application/json' }
        });
        if (elemRes.ok) {
          const el = await elemRes.json().catch(() => []);
          elementId = Array.isArray(el) && el[0]?.id ? Number(el[0].id) : null;
        }
      }
  
      const body = {
        user: userId,                    // обязателен
        kurs: parseInt(id, 10),          // обязателен
        ...(mavzuId ? { mavzu: mavzuId } : {}),
        ...(elementId ? { element: elementId } : {}),
        tartib: 1,
        elementtartib: 1
      };
  
      console.log('ENROLL BODY →', body); // важно: увидите, что реально уходит
  
      const postRes = await fetch('https://api.eduskill.uz/api/user-kurs/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });
  
      const postBody = await postRes.json().catch(() => null);
  
      if (!postRes.ok) {
        console.error('Enroll failed:', postRes.status, postBody);
        toast.error(
          (postBody && (postBody.detail || postBody.error || JSON.stringify(postBody))) ||
          (t('enrollFailed') || 'Ошибка при записи на курс.')
        );
        return;
      }
  
      toast.success(t('enrollSuccess') || 'Вы успешно записались на курс!');
      navigate(`/themes/${id}`);
    } catch (e) {
      console.error(t('networkError') || 'Сетевая ошибка', e);
      toast.error(t('networkError') || 'Произошла ошибка. Попробуйте позже.');
    } finally {
      setEnrolling(false);
    }
  };
  

  if (!course) return <p>Курс не найден</p>;
  if (error) return <p>Ошибка: {error}</p>;
  if (errorDesc) return <p>Ошибка: {errorDesc}</p>;

  return (
    <div>
      <div className={s.start__screen}>
        <section className={s.sec1}>
          <div className={s.start__screen__contents}>
            <p className={s.nd__content}>
              {handleLang(course.title_uz, course.title_ru, course.title_en)}
            </p>
            <div className="course__btns">
            <button
              className={s.start__screen__link}
              onClick={handleEnroll}
              disabled={enrolling || !localStorage.getItem('token') || !Number.isFinite(Number(user?.id))}
              aria-busy={enrolling}
            >
              {enrolling ? (t('processing') || 'Обработка…') : (t('courseLink') || 'Записаться на курс')}
            </button>

              <button className="register__btn btn__passive bgcinh" onClick={handleComment}>
                {t('courseRate') || 'Оценить курс'}
              </button>
            </div>
          </div>
          <div className={s.start__screen__img}>
            <img src={course.img_uz} alt="" />
          </div>
        </section>

        <section className={s.sec2}>
          <div className={s.cards}>
            {avzalliklar.map((avz, index) => (
              <div key={index} className={s.card}>
                <p className={s.card__title}>{handleLang(avz.title_uz, avz.title_ru, avz.title_en)}</p>
                <p className={s.card__txt}>{handleLang(avz.desc_uz, avz.desc_ru, avz.desc_en)}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="container">
        <div className={s.section2}>
          <p className={s.title}>
            {handleLang(description.title_uz, description.title_ru, description.title_en)}
          </p>
          <div className={s.squares}>
            {description.img_uz && (
              <img className={s.squares__img} src={description.img_uz} alt="" />
            )}
            <div className={s.square__infs}>
              <div className={`${s.square__inf} ${s.square__inf1} ${s.square__inf__txt}`}>
                {handleLang(description.desc_uz, description.desc_ru, description.desc_en)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="author container">
        <h2 className={s.title}>{t('author') || 'Автор'}</h2>
        {loadingAuthor && <p>Загрузка автора...</p>}
        {errorAuthor && <p style={{ color: 'crimson' }}>Ошибка: {String(errorAuthor)}</p>}
        {author && (
          <div className="author-block">
            <img src={author.img || "/images/author.jpg"} alt="" className="author-img" />
            <div className="author-content">
              <p className="author-name">
                {handleLang(author.first_name, author.first_name, author.first_name)}{' '}
                {handleLang(author.last_name, author.last_name, author.last_name)}
              </p>
              {/* <p className="author-deg">
                {author.degree || "Pedagogika fanlari bo‘yicha falsafa doktori (PhD), dotsent"}
              </p> */}
              <p className="author-desc">
                {author.description || ""}
              </p>
            </div>
          </div>
        )}
      </section>

      <SliderBigText />
      <TrainingProgram />
    </div>
  );
};
